import type { Scene } from '@babylonjs/core/scene';

export interface OverlayStatus {
  connection: string;
  room: string;
  peers: number;
  nodes: number;
  connections: number;
  mode: string;
}

/**
 * Frame rate and session status, as DOM.
 *
 * There was a copy of this on a panel parented to the XR camera, on the reasoning that frame
 * rate is the number that decides whether this is usable on a Quest and DOM is invisible inside
 * a session. It came out again: a translucent quad 65cm from the eye is a thing you look past
 * rather than at, it was a suspect for flicker in the room behind it, and in a shared session
 * the browser window is right there — `SessionScreen` reports the same numbers twice a second.
 *
 * If in-headset frame rate is needed again, put it somewhere in the world at a fixed spot, not
 * welded to the face.
 */
export class DebugOverlay {
  private readonly element: HTMLElement;

  private status: OverlayStatus = {
    connection: 'connecting',
    room: '—',
    peers: 0,
    nodes: 0,
    connections: 0,
    mode: 'desktop',
  };

  private fps = 0;
  private lastPaintedAt = 0;

  /** Removes the `onBeforeRenderObservable` registration below — the remover-returning
   *  shape `AvatarManager.start()`/`XrManager.stopSuppressionSync` already use, rather than
   *  holding the raw `Observer` and importing its type just to call `.remove()` on it here. */
  private readonly stopTicking: () => void;

  constructor(
    private readonly scene: Scene,
    container: HTMLElement,
  ) {
    this.element = document.createElement('div');
    this.element.className = 'overlay';
    container.appendChild(this.element);

    const observer = scene.onBeforeRenderObservable.add(() => this.tick());
    this.stopTicking = () => scene.onBeforeRenderObservable.remove(observer);
  }

  set(patch: Partial<OverlayStatus>): void {
    this.status = { ...this.status, ...patch };
    this.paint();
  }

  /**
   * Not called anywhere outside a test until the `/room` route's own cleanup started
   * calling it (`main.ts`) — before that, `#status` sat outside the router's `#portal`
   * mount (`main.ts` builds this element straight into `#status`, never into the routed
   * mount), so nothing about leaving `/room` ever removed it, and a second `/room` mount
   * (Router exists specifically to make that a live path) stacked a second overlay on top
   * of the first. Removing the render-loop registration here too, not just the DOM node:
   * left unremoved, it would keep calling `this.tick()` — reading `this.scene`, itself
   * already disposed by the time this runs (`main.ts` disposes this before `sceneRoot`, but
   * a caller that got the order wrong would otherwise hit a disposed engine) — for the rest
   * of the scene's lifetime for no live overlay to paint into.
   */
  dispose(): void {
    this.stopTicking();
    this.element.remove();
  }

  private tick(): void {
    this.fps = this.scene.getEngine().getFps();

    // Twice a second. Rewriting the DOM every frame would cost more than the number it reports
    // is worth, and nobody reads it faster than that.
    const now = performance.now();
    if (now - this.lastPaintedAt < 500) {
      return;
    }

    this.lastPaintedAt = now;
    this.paint();
  }

  private paint(): void {
    const fps = Math.round(this.fps);
    const { connection, room, peers, nodes, connections, mode } = this.status;

    this.element.innerHTML = `
      <span class="metric"><b>${fps}</b> fps</span>
      <span class="metric">${connection}</span>
      <span class="metric">${room}</span>
      <span class="metric">${peers} peer${peers === 1 ? '' : 's'}</span>
      <span class="metric">${nodes} nodes / ${connections} edges</span>
      <span class="metric">${mode}</span>
    `;
  }
}
